import { Model } from '@singularsystems/neo-core';
import { reaction } from "mobx";
import { CancellableEvent } from '../Misc';
/**
 * Tab manager used to manage a tab component.
 * @param T string union type of tab names.
 */
var TabManager = /** @class */ (function () {
    /**
     * Creates a tab manager used to bind to a tab container.
     * @param selectedTab Initially selected tab.
     * @param autoSelectFirstTab Set to true if the first child tab should become selected.
     */
    function TabManager(selectedTab, autoSelectFirstTab, observable) {
        if (selectedTab === void 0) { selectedTab = ""; }
        if (autoSelectFirstTab === void 0) { autoSelectFirstTab = false; }
        var _this = this;
        this.autoSelectFirstTab = autoSelectFirstTab;
        this.tabs = {};
        if (observable) {
            this.observable = observable;
        }
        else {
            this.observable = new Model.ObservableValue(selectedTab);
        }
        this.onTabChanged = this.onTabChanged.bind(this);
        this.reactionDisposer = reaction(function () { return _this.observable.value; }, function () { return _this.onTabChanged(); });
    }
    /** Called by tab container. */
    TabManager.prototype.initialise = function () {
        if (this.selectedTab !== "") {
            this.onTabChanged();
        }
    };
    /** Called by tab container. */
    TabManager.prototype.onUnmount = function () {
    };
    Object.defineProperty(TabManager.prototype, "selectedTab", {
        get: function () {
            return this.observable.value;
        },
        set: function (value) {
            var _this = this;
            if (!this.tabs[value]) {
                // Probably different case, find the actual tab key.
                var foundValue = Object.keys(this.tabs).find(function (t) { return t.toLowerCase() === value.toLowerCase(); });
                if (foundValue) {
                    value = foundValue;
                }
            }
            var currentTabKey = this.observable.peek;
            if (value !== currentTabKey) {
                var cancellableEvent = new CancellableEvent(function () { return currentTabKey === _this.observable.peek; });
                var currentTab = this.tabs[currentTabKey];
                if ((currentTab === null || currentTab === void 0 ? void 0 : currentTab.onLeave) !== undefined) {
                    currentTab.onLeave(cancellableEvent);
                }
                cancellableEvent.tryRunAction(function () {
                    _this.observable.value = value;
                });
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Registers a callback to run when the tab is first displayed, or when reset is called with reInitialise=true.
     */
    TabManager.prototype.onTabInitialise = function (tabKey, callback) {
        var tab = this.getTab(tabKey, true);
        tab.onInitialise = callback;
        return this;
    };
    /**
     * Registers a callback to run whenever the tab with this tab key is displayed.
     */
    TabManager.prototype.onTabDisplayed = function (tabKey, callback) {
        var tab = this.getTab(tabKey, true);
        tab.onDisplayed = callback;
        return this;
    };
    /** Called by tab container. */
    TabManager.prototype.tabMounted = function (tabKey, onLeave) {
        var tab = this.getTab(tabKey, true);
        tab.onLeave = onLeave;
        if (!this.selectedTab && this.autoSelectFirstTab) {
            this.selectedTab = tabKey;
        }
    };
    TabManager.prototype.getTab = function (tabKey, create) {
        if (create === void 0) { create = false; }
        var tab = this.tabs[tabKey];
        if (!tab && create) {
            tab = new Tab();
            this.tabs[tabKey] = tab;
        }
        return tab;
    };
    /**
     * Marks the tab as 'initial'.
     * @param reInitialise True if the tabs onInitialise() should be invoked.
     */
    TabManager.prototype.reset = function (tabKey, reInitialise) {
        if (reInitialise === void 0) { reInitialise = false; }
        var tab = this.getTab(tabKey);
        if (tab) {
            if (reInitialise) {
                tab.initialise();
            }
            else {
                tab.hasFetched = false;
            }
        }
    };
    /** Calls onInitialise() if the tab has not yet initialised. */
    TabManager.prototype.initialiseTab = function (tabKey) {
        var tab = this.getTab(tabKey);
        if (tab && !tab.hasFetched) {
            tab.initialise();
        }
    };
    /**
     * Marks all tabs as 'initial', causing onInitialise() to be called the next time each is displayed.
     * @param reInitialise True if the onInitialise() should be called on the currently displayed tab.
     */
    TabManager.prototype.resetAll = function (reInitialise) {
        if (reInitialise === void 0) { reInitialise = false; }
        var currentTabKey = this.observable.peek;
        for (var tabKey in this.tabs) {
            this.reset(tabKey, reInitialise && currentTabKey === tabKey);
        }
    };
    TabManager.prototype.onTabChanged = function () {
        var tab = this.getTab(this.observable.peek);
        if (tab) {
            if (!tab.hasFetched) {
                tab.initialise();
            }
            if (tab.onDisplayed) {
                tab.onDisplayed();
            }
        }
    };
    TabManager.prototype.dispose = function () {
        this.reactionDisposer();
    };
    return TabManager;
}());
export { TabManager };
var Tab = /** @class */ (function () {
    function Tab() {
        this.hasFetched = false;
    }
    /**
     * Calls initialise without checking hasFetched.
     */
    Tab.prototype.initialise = function () {
        if (this.onInitialise) {
            this.onInitialise();
        }
        this.hasFetched = true;
    };
    return Tab;
}());
